import { FC, useMemo } from 'react'
import { ReplaceCardPaneStep } from 'components/features/creditCard/creditCardStore/floatingPane'
import { CreditCardPanePreset } from 'components/features/creditCard/creditCardPanes/CreditCardPanePreset'
import { CCReplaceReason } from 'components/features/creditCard/creditCardStore/types'
import { useForm } from 'react-hook-form'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { currentCardSelector } from 'components/features/creditCard/creditCardStore/selectors'
import { CCReplaceCardAction } from 'components/features/creditCard/creditCardStore/actions'
import Button from 'components/features/pfm/Button/Button'
import CardShippingAddress from 'components/features/creditCard/creditCardPanes/paneComponents/CardShippingAddress'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import { BACK_BUTTON_PROPS, CANCEL_BUTTON_PROPS, CardMenuPaneButton, KillAllPanesButton } from 'components/features/pfm/Button/FloatingPaneButtons'
import { FormPreset } from 'components/atoms/inputs/FormPreset/FormPreset'
import { LabeledInput } from 'components/atoms/inputs/LabeledInput/LabeledInput'
import { CC_SUPPORT_PHONE_NUMBER } from 'components/features/creditCard/creditCardStore/constants'
import Flex from 'components/atoms/Flex/Flex'

export type ReplaceCardReasonForm = {
  reason: CCReplaceReason
}

type ReplaceCardPaneProps = {
  step: ReplaceCardPaneStep
  reason?: CCReplaceReason
}

const ReplaceCardPane: FC<ReplaceCardPaneProps> = ({ step, reason: reasonProp }) => {
  const formContext = useForm<ReplaceCardReasonForm>({
    defaultValues: {
      reason: 'damaged',
    },
  })

  const { handleSubmit } = formContext
  const card = useStoreSelector(currentCardSelector)
  const isLoading = useStoreSelector(({ app }) => app.creditCard.replaceCardRequest.isLoading)
  const dispatch = useStoreDispatch()

  const handleSuccess = useMemo(() => handleSubmit((values) => {
    const { reason } = values
    if (!reason || !card) {
      return
    }
    const { uid } = card
    dispatch(new CCReplaceCardAction({ reason, uid }))
  }), [card, dispatch, handleSubmit])

  const reasonFormChild = useMemo(() => (
    <>
      <h2>Tell us the main reason for replacing your card</h2>
      <FormPreset formContext={ formContext } onSubmit={ handleSuccess }>
        <Flex component='fieldset' marginBottom='small'>
          <LabeledInput
            type='radio'
            name='reason'
            label='Replacement Reason'
            style='outlined'
            options={ [{
              value: 'damaged',
              label: 'Damaged Card',
              note: 'Your new card will have the same number',
            }, {
              value: 'compromised',
              label: 'Lost / Stolen Card',
              note: 'Your new card will have a different number',
            }] }
          />
        </Flex>
        <section>
          <p>It should arrive within 7-10 business days at this address:</p>
          <CardShippingAddress />
        </section>
        <Button text='Submit Replace Request' submit />
      </FormPreset>
      <small>
        If your card is already in your digital wallet, you can keep using it in
        stores, online, and in apps as a secure way to pay while you wait for
        the replacement.
      </small>
    </>
  ), [formContext, handleSuccess])

  const resultHeader = useMemo(() => (
    <>
      <figure>
        <LazyLoadImage
          alt='Status card in envelope'
          src='images/credit-card-in-envelope.png'
          width='100%'
        />
      </figure>
      <h2>Your new card is on its way!</h2>
    </>
  ), [])

  const resultChild = useMemo(() => reasonProp === 'damaged' ? (
    <>
      { resultHeader }
      <p>
        If you're replacing a damaged card, you can still use it until you
        activate your new card.
      </p>
      <small>
        To learn more, please see the Consumer Credit Card Agreement.
      </small>
    </>
  ) : (
    <>
      { resultHeader }
      <p>
        If you think someone used your card without your permission, please call
        customer support right away at{' '}
        <a href={ 'tel:' + CC_SUPPORT_PHONE_NUMBER }>{ CC_SUPPORT_PHONE_NUMBER }</a> for help.
      </p>
      <small>
        To learn more, please see the Consumer Credit Card Agreement “Disputed
        Transactions” and “Security and Lost or Stolen Card”
      </small>
    </>
  ), [reasonProp, resultHeader])

  return (
    <CreditCardPanePreset
      title='Replace Card'
      isLoading={ isLoading }
      primaryButton={ step !== 'reason' ? <KillAllPanesButton { ...CANCEL_BUTTON_PROPS } text='Close' style='solid' /> : null }
      secondaryButton={ step === 'reason' ? <CardMenuPaneButton { ...BACK_BUTTON_PROPS } /> : null }
      tertiaryButton={ step === 'reason' ? <KillAllPanesButton { ...CANCEL_BUTTON_PROPS } text='Close' /> : null }
    >
      { step === 'reason' ? reasonFormChild : resultChild }
    </CreditCardPanePreset>
  )
}

export default ReplaceCardPane
